import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button} from '@mui/material';
import {useSnackbar} from 'notistack';

import {BannerDetail, useUpdateBanner} from '@entities/banner';
import {useGetGeoFilterOptions, useUploadAdminFile} from '@entities/common';
import {getBackendAssetPath} from '@shared/api';
import {CardBox} from '@shared/components';
import {createEmptyTranslationDict, resolveEnglishText} from '@shared/helpers';
import {TranslationDict} from '@shared/types';
import {toPickerDateInTz} from '@shared/utils';
import {FormRow} from '@widgets/common';
import {FormFields} from '../CreateBanner/FormFields';
import {FormValues, v8nSchema} from '../CreateBanner/v8nSchema';

import {COLOR__WHITE} from '@/theme/colors';

interface Props {
  banner: BannerDetail;
  onClose: () => void;
}

export const EditBannerForm: FC<Props> = ({banner, onClose}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: updateBanner, isPending: isUpdating} = useUpdateBanner();
  const {mutateAsync: uploadAdminFile, isPending: isUploading} =
    useUploadAdminFile();
  const {data: geoOptions = []} = useGetGeoFilterOptions();

  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    () => banner.nameTranslations ?? createEmptyTranslationDict(),
  );

  const formMethods = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      name: banner.name,
      logoFile: null,
      appUrl: banner.appUrl ?? '',
      webUrl: banner.webUrl ?? '',
      activeFrom: banner.activeFrom
        ? toPickerDateInTz(banner.activeFrom)
        : null,
      activeTo: banner.activeTo ? toPickerDateInTz(banner.activeTo) : null,
      geos: geoOptions.filter(opt => banner.geos.includes(opt.id)),
      order: banner.order,
    },
  });
  const {
    handleSubmit,
    formState: {dirtyFields},
  } = formMethods;

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      const uploadedLogo = values.logoFile
        ? await uploadAdminFile({file: values.logoFile, type: 'category_logo'})
        : null;

      await updateBanner({
        id: banner.id,
        nameTrans: {
          ...nameTranslations,
          en: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        logoUrl: uploadedLogo?.key ?? getBackendAssetPath(banner.logoUrl),
        appUrl: values.appUrl || null,
        webUrl: values.webUrl || null,
        activeFrom: values.activeFrom,
        activeTo: values.activeTo,
        geos: values.geos.map(g => g.id),
        order: values.order,
      });

      enqueueSnackbar('Banner updated', {variant: 'success'});
      onClose();
    } catch {
      enqueueSnackbar('Failed to update banner', {variant: 'error'});
    }
  };

  return (
    <CardBox bgcolor={COLOR__WHITE} maxWidth={600}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormProvider {...formMethods}>
          <FormFields
            nameTranslations={nameTranslations}
            onNameTranslationsChange={setNameTranslations}
            existingLogoUrl={banner.logoUrl}
          />
        </FormProvider>

        <FormRow width={420} mt={3}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={isUpdating || isUploading}
            sx={{flex: 2}}
          >
            Apply changes
          </Button>
        </FormRow>
      </form>
    </CardBox>
  );
};
